import React, { useState } from 'react';
import { Eye, FileText, BarChart3, CheckCircle, AlertCircle, Download, Edit } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PreviewReport = ({ onSubmit, initialData = {} }) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    previewMode: initialData.previewMode || 'full',
    showComments: initialData.showComments || true,
    showPageNumbers: initialData.showPageNumbers || true,
    showTableOfContents: initialData.showTableOfContents || true,
    reviewNotes: initialData.reviewNotes || '',
    approvalStatus: initialData.approvalStatus || 'pending'
  });

  const reportSections = [
    {
      id: 1,
      title: t('workflow.draftReport.previewReport.reportSections.executiveSummary'),
      status: 'completed',
      pageCount: 2,
      hasCharts: true,
      hasData: true
    },
    {
      id: 2,
      title: t('workflow.draftReport.previewReport.reportSections.methodology'),
      status: 'completed',
      pageCount: 3,
      hasCharts: false,
      hasData: true
    },
    {
      id: 3,
      title: t('workflow.draftReport.previewReport.reportSections.dataCollection'),
      status: 'completed',
      pageCount: 4,
      hasCharts: true,
      hasData: true
    },
    {
      id: 4,
      title: t('workflow.draftReport.previewReport.reportSections.calculations'),
      status: 'completed',
      pageCount: 6,
      hasCharts: true,
      hasData: true
    },
    {
      id: 5,
      title: t('workflow.draftReport.previewReport.reportSections.materialityAnalysis'),
      status: 'completed',
      pageCount: 3,
      hasCharts: true,
      hasData: true
    },
    {
      id: 6,
      title: t('workflow.draftReport.previewReport.reportSections.esrsDisclosures'),
      status: 'completed',
      pageCount: 8,
      hasCharts: false,
      hasData: true
    },
    {
      id: 7,
      title: t('workflow.draftReport.previewReport.reportSections.appendices'),
      status: 'completed',
      pageCount: 5,
      hasCharts: true,
      hasData: true
    }
  ];

  const previewModes = [
    { value: 'full', label: t('workflow.draftReport.previewReport.previewModes.full.label'), description: t('workflow.draftReport.previewReport.previewModes.full.description') },
    { value: 'summary', label: t('workflow.draftReport.previewReport.previewModes.summary.label'), description: t('workflow.draftReport.previewReport.previewModes.summary.description') },
    { value: 'technical', label: t('workflow.draftReport.previewReport.previewModes.technical.label'), description: t('workflow.draftReport.previewReport.previewModes.technical.description') }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in-progress':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'pending':
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'in-progress':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'pending':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return t('workflow.draftReport.previewReport.status.completed');
      case 'in-progress':
        return t('workflow.draftReport.previewReport.status.inProgress');
      case 'pending':
        return t('workflow.draftReport.previewReport.status.pending');
      default:
        return t('workflow.draftReport.previewReport.status.pending');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const totalPages = reportSections.reduce((sum, section) => sum + section.pageCount, 0);
  const completedSections = reportSections.filter(section => section.status === 'completed').length;
  const totalSections = reportSections.length;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="flex items-center mb-6">
          <Eye className="w-8 h-8 text-blue-500 mr-3" />
          <div>
            <h2 className="text-2xl font-bold text-gray-800">{t('workflow.draftReport.previewReport.title')}</h2>
            <p className="text-gray-600">{t('workflow.draftReport.previewReport.description')}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Preview Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('workflow.draftReport.previewReport.previewMode')}
              </label>
              <select
                value={formData.previewMode}
                onChange={(e) => handleInputChange('previewMode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {previewModes.map(mode => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('workflow.draftReport.previewReport.approvalStatus')}
              </label>
              <select
                value={formData.approvalStatus}
                onChange={(e) => handleInputChange('approvalStatus', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="under-review">Under Review</option>
              </select>
            </div>
          </div>

          {/* Display Options */}
          <div className="flex items-center space-x-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.showComments}
                onChange={(e) => handleInputChange('showComments', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">{t('workflow.draftReport.previewReport.showComments')}</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.showPageNumbers}
                onChange={(e) => handleInputChange('showPageNumbers', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">{t('workflow.draftReport.previewReport.showPageNumbers')}</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.showTableOfContents}
                onChange={(e) => handleInputChange('showTableOfContents', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">{t('workflow.draftReport.previewReport.showTableOfContents')}</span>
            </label>
          </div>

          {/* Report Sections Overview */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Report Sections</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reportSections.map(section => (
                <div
                  key={section.id}
                  className={`p-4 border rounded-lg ${getStatusColor(section.status)}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium">{section.title}</h4>
                    {getStatusIcon(section.status)}
                  </div>
                  
                  <div className="text-sm space-y-1">
                    <p>{section.pageCount} {t('workflow.draftReport.previewReport.pageCount')}</p>
                    <div className="flex items-center space-x-4 text-xs">
                      {section.hasCharts && (
                        <span className="flex items-center">
                          <BarChart3 className="w-3 h-3 mr-1" />
                          {t('workflow.draftReport.previewReport.hasCharts')}
                        </span>
                      )}
                      {section.hasData && (
                        <span className="flex items-center">
                          <FileText className="w-3 h-3 mr-1" />
                          {t('workflow.draftReport.previewReport.hasData')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Review Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('workflow.draftReport.previewReport.reviewNotes')}
            </label>
            <textarea
              value={formData.reviewNotes}
              onChange={(e) => handleInputChange('reviewNotes', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('workflow.draftReport.previewReport.reviewNotesPlaceholder')}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200"
            >
              <Edit className="w-4 h-4 inline mr-2" />
              Edit Report
            </button>
            <button
              type="button"
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-200"
            >
              <Download className="w-4 h-4 inline mr-2" />
              Download Preview
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default PreviewReport;
